import { FC, useEffect, useMemo, useState } from 'react';
import PaginationBar from '../../components/PaginationBar';
import { DreamPrice, fetchDreamPrices } from '../../api/dreamPrices';
import { runSyncJob } from '../../api/syncData';

interface DreamPricesPanelProps {
  onSelect?: (id: string) => void;
}

interface DreamPricesState {
  records: DreamPrice[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const formatDate = (value: string | null) => {
  if (!value) {
    return '—';
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString();
};

const formatProducts = (products: DreamPrice['products']) => {
  if (!products || products.length === 0) {
    return '—';
  }

  const labels = products.map((product) => {
    if (product.name && product.name.trim()) {
      return product.name;
    }

    if (product.productId ?? product.id) {
      const identifier = product.productId ?? product.id;
      return `Product #${identifier}`;
    }

    return 'Product';
  });

  if (labels.length <= 2) {
    return labels.join(', ');
  }

  return `${labels.slice(0, 2).join(', ')} +${labels.length - 2} more`;
};

const DreamPricesPanel: FC<DreamPricesPanelProps> = ({ onSelect }) => {
  const [page, setPage] = useState(1);
  const limit = 20;
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [state, setState] = useState<DreamPricesState>({
    records: [],
    total: 0,
    page: 1,
    limit,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const handle = window.setTimeout(
      () => setDebouncedSearch(search.trim()),
      350,
    );

    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    fetchDreamPrices({ page, limit, search: debouncedSearch || undefined })
      .then((response) => {
        if (!isMounted) {
          return;
        }
        setState({
          records: response.data,
          total: response.total,
          page: response.page,
          limit: response.limit,
          totalPages: response.totalPages,
        });
      })
      .catch((err) => {
        if (!isMounted) {
          return;
        }
        setError(err instanceof Error ? err.message : 'Failed to load dream prices.');
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [page, limit, debouncedSearch, refreshCounter]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(state.total / limit || 1)),
    [state.total, limit],
  );

  const triggerRefresh = () => setRefreshCounter((prev) => prev + 1);

  const handleSync = async () => {
    if (syncing) {
      return;
    }

    setSyncing(true);
    try {
      await runSyncJob('dreamPrices');
      triggerRefresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sync dream prices.';
      setError(message);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white px-6 py-5 shadow-inner">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search dream prices..."
          className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 sm:w-72"
        />

        <button
          type="button"
          className="inline-flex items-center justify-center rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
          onClick={handleSync}
          disabled={syncing}
        >
          {syncing ? 'Syncing…' : 'Sync from ERP'}
        </button>
      </div>

      <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
        <table className="min-w-full text-left text-sm text-slate-600">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3 font-semibold">ERP ID</th>
              <th className="px-5 py-3 font-semibold">Status</th>
              <th className="px-5 py-3 font-semibold">Start Date</th>
              <th className="px-5 py-3 font-semibold">End Date</th>
              <th className="px-5 py-3 font-semibold">Products</th>
              <th className="px-5 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="px-5 py-6 text-center text-slate-400" colSpan={6}>
                  Loading dream prices...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td className="px-5 py-6 text-center text-danger" colSpan={6}>
                  {error}
                </td>
              </tr>
            ) : state.records.length === 0 ? (
              <tr>
                <td className="px-5 py-6 text-center text-slate-400" colSpan={6}>
                  No dream prices found.
                </td>
              </tr>
            ) : (
              state.records.map((dreamPrice) => {
                const productsLabel = formatProducts(dreamPrice.products);
                const productCount = dreamPrice.products?.length ?? 0;
                const locationCount = dreamPrice.locations?.length ?? 0;

                return (
                  <tr key={dreamPrice.id} className="even:bg-slate-50/60">
                    <td className="px-5 py-3 font-medium text-slate-700">{dreamPrice.erpId}</td>
                    <td className="px-5 py-3 text-slate-500">{dreamPrice.status ?? '—'}</td>
                    <td className="px-5 py-3 text-slate-500">{formatDate(dreamPrice.startDate)}</td>
                    <td className="px-5 py-3 text-slate-500">{formatDate(dreamPrice.endDate)}</td>
                    <td className="px-5 py-3 text-slate-500">
                      <div>{productsLabel}</div>
                      <div className="mt-1 text-xs text-slate-400">
                        {productCount} product{productCount === 1 ? '' : 's'}
                        {locationCount ? ` • ${locationCount} location${locationCount === 1 ? '' : 's'}` : ''}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button
                        type="button"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-primary transition hover:bg-primary/10"
                        onClick={() => onSelect?.(dreamPrice.id)}
                        disabled={!onSelect}
                      >
                        👁️
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <PaginationBar
        page={state.page}
        totalPages={totalPages}
        onPrev={() => setPage((prev) => Math.max(1, prev - 1))}
        onNext={() => setPage((prev) => Math.min(totalPages, prev + 1))}
        onSelect={(num) => setPage(num)}
        disabled={loading}
        renderSummary={
          <span>
            Showing {(state.page - 1) * limit + Math.min(limit, state.records.length)} of {state.total}{' '}
            dream prices
          </span>
        }
        renderExtra={
          <span className="text-xs text-slate-400">Page {state.page} of {totalPages} ({limit} / page)</span>
        }
      />
    </div>
  );
};

export default DreamPricesPanel;
